import { useDetailViewState } from './DetailReturnState';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FiHeart, FiMessageSquare, FiFlag } from 'react-icons/fi';
import { commentApi } from '../../../../api/comment';
import ProfileIdentity from '../../../../components/content/ProfileIdentity';
import { countComments, flattenReplies, reviewDetailApi } from './reviewDetailModel';
import { useReviewDraft, useConfirmReviewDrafts } from './ReviewDraftProtection';
export function ReviewAvatar({ user, size = 32 }) {
  const [failed, setFailed] = useState(null);
  return (
    <span className="pp-review-avatar" style={{ width: size, height: size }}>
      {user.nickname?.slice(0, 1)}
      {user.profileImageUrl && failed !== user.profileImageUrl && (
        <img src={user.profileImageUrl} alt="" onError={() => setFailed(user.profileImageUrl)} />
      )}
    </span>
  );
}
function Composer({ en, user, onLogin, onSave, inputRef, initial = '', onCancel, label }) {
  const [text, setText] = useState(initial),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false);
  const pending = useRef(false),
    mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const discard = useReviewDraft(text !== initial, busy, en);
  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      onLogin();
      return;
    }
    if (!text.trim() || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(false);
    try {
      await onSave(text.trim());
      if (mounted.current) setText('');
    } catch {
      if (mounted.current) setError(true);
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <form className="pp-review-composer" onSubmit={submit}>
      <textarea
        ref={inputRef}
        aria-label={label || (en ? 'Comment' : '댓글 내용')}
        placeholder={
          !user
            ? en
              ? 'Log in to write a comment'
              : '로그인 후 댓글을 작성할 수 있습니다'
            : label === (en ? 'Reply' : '답글 내용')
              ? en
                ? 'Write a reply'
                : '답글을 입력하세요'
              : en
                ? 'Write a comment'
                : '댓글을 입력하세요'
        }
        value={text}
        disabled={busy}
        onChange={(e) => setText(e.target.value)}
      />
      <div>
        {onCancel && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (discard()) onCancel();
            }}
          >
            {en ? 'Cancel' : '취소'}
          </button>
        )}
        <button type="submit" disabled={busy || (Boolean(user) && !text.trim())}>
          {!user
            ? en
              ? 'Log In to Comment'
              : '로그인하고 댓글 쓰기'
            : busy
              ? en
                ? 'Saving…'
                : '저장 중…'
              : initial
                ? en
                  ? 'Save'
                  : '저장'
                : en
                  ? label === 'Reply'
                    ? 'Post Reply'
                    : 'Post Comment'
                  : label === '답글 내용'
                    ? '답글 등록'
                    : '댓글 등록'}
        </button>
      </div>
      {error && (
        <p role="alert">
          {en
            ? 'Could not save. Your text is kept. Try again.'
            : '저장하지 못했어요. 작성 내용은 유지됩니다. 다시 시도해 주세요.'}
        </p>
      )}
    </form>
  );
}
function CommentRow({
  comment,
  depth = 0,
  en,
  user,
  onLogin,
  onReport,
  run,
  refresh,
  onCreate,
  targetId,
  updateCached,
}) {
  const [editing, setEditing] = useState(false),
    [replying, setReplying] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false),
    [deleting, setDeleting] = useState(false);
  const children = depth === 0 ? flattenReplies(comment.replies) : [];
  const targetIndex = children.findIndex((c) => String(c.id) === String(targetId));
  const [visible, setVisible] = useDetailViewState(`replies:${comment.id}`, () =>
    targetIndex < 0 ? 0 : targetIndex + 1,
  );
  const target = useRef(null),
    pending = useRef(false),
    alive = useRef(true);
  const isOwner = user && String(user.id) === String(comment.user?.id),
    canDelete = isOwner || user?.role === 'ADMIN';
  useReviewDraft(false, busy, en);
  const confirmDrafts = useConfirmReviewDrafts(en);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (String(comment.id) === String(targetId))
      target.current?.scrollIntoView?.({ block: 'center' });
  }, [targetId, comment.id]);
  useEffect(() => {
    if (targetIndex >= 0) setVisible((v) => Math.max(v, targetIndex + 1));
  }, [targetIndex, setVisible]);
  const action = async (fn, apply) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(false);
    try {
      await run(fn);
      if (alive.current) {
        apply?.();
        setDeleting(false);
        await refresh();
      }
    } catch {
      if (alive.current) setError(true);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  };
  return (
    <article
      ref={target}
      className={`pp-review-comment ${depth ? 'is-reply' : ''} ${String(comment.id) === String(targetId) ? 'is-target' : ''}`}
      data-comment-id={comment.id}
    >
      <div className="pp-review-comment-top">
        <Link
          to={`/users/${encodeURIComponent(comment.user?.nickname || '')}`}
          aria-label={`${comment.user?.nickname} ${en ? 'profile' : '프로필 보기'}`}
        >
          <ReviewAvatar user={comment.user || {}} size={20} />
          <ProfileIdentity
            avatar={false}
            name={comment.user?.nickname || ''}
            mark={comment.user?.profileMark}
          />
        </Link>
        <time>{comment.createdAt}</time>
      </div>
      {editing ? (
        <Composer
          en={en}
          user={user}
          initial={comment.content}
          label={en ? 'Edit comment' : '댓글 수정'}
          onCancel={() => setEditing(false)}
          onSave={async (text) => {
            await run(() =>
              commentApi.updateComment(comment.id, text, { suppressGlobalError: true }),
            );
            updateCached(comment.id, { content: text });
            setEditing(false);
            await refresh();
          }}
        />
      ) : (
        <p className="pp-review-comment-body">
          {comment.isDeleted ? (
            en ? (
              'This comment was deleted.'
            ) : (
              '삭제된 댓글입니다.'
            )
          ) : (
            <>
              {comment.mentionedUser && (
                <span className="pp-review-mention">@{comment.mentionedUser.nickname} </span>
              )}
              {comment.content}
            </>
          )}
        </p>
      )}
      {!comment.isDeleted && !editing && (
        <div className="pp-review-comment-actions">
          <button
            aria-label={en ? 'Like comment' : '댓글 좋아요'}
            aria-pressed={Boolean(comment.isLiked)}
            disabled={busy}
            onClick={() =>
              user
                ? action(
                    () =>
                      comment.isLiked
                        ? commentApi.deleteLike(comment.id, { suppressGlobalError: true })
                        : commentApi.addLike(comment.id, { suppressGlobalError: true }),
                    () =>
                      updateCached(comment.id, {
                        isLiked: !comment.isLiked,
                        likeCount: Math.max(
                          0,
                          (comment.likeCount || 0) + (comment.isLiked ? -1 : 1),
                        ),
                      }),
                  )
                : onLogin()
            }
          >
            <FiHeart size={16} fill={comment.isLiked ? 'currentColor' : 'none'} />
            {comment.likeCount ?? 0}
          </button>
          <button
            disabled={busy}
            onClick={() => (user ? confirmDrafts() && setReplying((v) => !v) : onLogin())}
          >
            <FiMessageSquare size={16} />
            {en ? 'Reply' : '답글'}
          </button>
          {isOwner && (
            <button disabled={busy} onClick={() => setEditing(true)}>
              {en ? 'Edit' : '수정'}
            </button>
          )}
          {canDelete && (
            <button disabled={busy} onClick={() => setDeleting(true)}>
              {en ? 'Delete' : '삭제'}
            </button>
          )}
          {!isOwner && user && (
            <button
              disabled={busy}
              onClick={() =>
                onReport({
                  contentType: 'COMMENT',
                  contentId: comment.id,
                  reportedUserId: comment.user.id,
                  displayName: comment.user.nickname,
                })
              }
            >
              <FiFlag size={14} />
              {en ? 'Report' : '신고'}
            </button>
          )}
        </div>
      )}
      {deleting && (
        <div role="alertdialog" aria-label={en ? 'Delete comment' : '댓글 삭제'}>
          <p>{en ? 'Delete this comment?' : '댓글을 삭제하시겠습니까?'}</p>
          <button disabled={busy} onClick={() => setDeleting(false)}>
            {en ? 'Cancel' : '취소'}
          </button>
          <button
            disabled={busy}
            onClick={() =>
              action(
                async () => {
                  try {
                    await commentApi.deleteComment(comment.id, { suppressGlobalError: true });
                  } catch (e) {
                    if (e?.response?.status !== 404) throw e;
                  }
                },
                () => updateCached(comment.id, null),
              )
            }
          >
            {en ? 'Delete' : '삭제'}
          </button>
        </div>
      )}
      {error && (
        <p role="alert">
          {en ? 'Could not complete. Try again.' : '처리하지 못했어요. 다시 시도해 주세요.'}
        </p>
      )}
      {replying && (
        <div className="pp-review-reply-editor">
          <Composer
            en={en}
            user={user}
            label={en ? 'Reply' : '답글 내용'}
            onSave={async (text) => {
              await onCreate(text, comment);
              setReplying(false);
              setVisible((v) => v + 5);
            }}
            onCancel={() => setReplying(false)}
          />
        </div>
      )}
      {children.slice(0, visible).map((c) => (
        <CommentRow
          key={c.id}
          comment={c}
          depth={1}
          {...{ en, user, onLogin, onReport, run, refresh, onCreate, targetId, updateCached }}
        />
      ))}
      {children.length > visible && (
        <button
          className="pp-review-replies"
          onClick={() => setVisible((v) => Math.min(children.length, v + 5))}
        >
          {en
            ? visible
              ? `View ${Math.min(5, children.length - visible)} more replies`
              : `View ${children.length} replies`
            : visible
              ? `답글 ${Math.min(5, children.length - visible)}개 더 보기`
              : `답글 ${children.length}개 보기`}
        </button>
      )}
      {visible >= children.length && visible > 0 && (
        <button
          className="pp-review-replies"
          onClick={() => {
            if (confirmDrafts()) setVisible(0);
          }}
        >
          {en ? 'Hide replies' : '답글 숨기기'}
        </button>
      )}
    </article>
  );
}
export default function ReviewComments({
  reviewId,
  en,
  user,
  onLogin,
  onReport,
  assertAccount,
  onCount,
  inputRef,
  targetId,
  threadApi = reviewDetailApi,
  queryNamespace = 'reviewComments',
  onLoadingChange,
}) {
  const [sort, setSort] = useDetailViewState(`comment-sort:${queryNamespace}:${reviewId}`, 'likes'),
    [createdTarget, setCreatedTarget] = useState(null);
  const client = useQueryClient(),
    confirmDrafts = useConfirmReviewDrafts(en);
  const queryKey = [queryNamespace, reviewId, user?.id ?? 'guest', sort];
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => threadApi.listComments(reviewId, sort, signal),
    retry: false,
  });
  useEffect(() => {
    onLoadingChange?.(query.isLoading);
  }, [query.isLoading, onLoadingChange]);
  const comments = query.data || [],
    count = countComments(comments);
  useEffect(() => {
    if (query.data) onCount(count);
  }, [query.data, count, onCount]);
  const run = async (fn) => {
    assertAccount();
    const result = await fn();
    assertAccount();
    return result;
  };
  const refresh = () =>
    client.invalidateQueries({ queryKey: [queryNamespace, reviewId, user?.id ?? 'guest'] });
  const prefix = [queryNamespace, reviewId, user?.id ?? 'guest'];
  const updateCached = (id, patch) => {
    const update = (items) =>
      items.flatMap((c) => {
        if (c.id === id)
          return patch
            ? [{ ...c, ...patch }]
            : c.replies?.length
              ? [{ ...c, isDeleted: true }]
              : [];
        return [{ ...c, replies: update(c.replies || []) }];
      });
    client.setQueriesData({ queryKey: prefix }, (items) => (items ? update(items) : items));
  };
  const create = async (text, parent) => {
    const created = await run(() => threadApi.createComment(reviewId, text, parent));
    for (const [key, items] of client.getQueriesData({ queryKey: prefix })) {
      if (!items) continue;
      const insertReply = (list) =>
        list.map((c) =>
          c.id === parent?.id
            ? { ...c, replies: [...(c.replies || []).filter((r) => r.id !== created.id), created] }
            : { ...c, replies: insertReply(c.replies || []) },
        );
      const withoutDuplicate = items.filter((c) => c.id !== created.id);
      client.setQueryData(
        key,
        parent
          ? insertReply(items)
          : key[3] === 'latest'
            ? [created, ...withoutDuplicate]
            : [...withoutDuplicate, created],
      );
    }
    setCreatedTarget(created.id);
    await refresh();
  };
  return (
    <section className="pp-review-comments">
      <div className="pp-review-comments-heading">
        <h2>
          {en ? 'Comments' : '댓글'} ({count})
        </h2>
        <div>
          {[
            ['likes', en ? 'Most Liked' : '공감순'],
            ['latest', en ? 'Newest' : '최신순'],
          ].map(([value, label]) => (
            <button
              key={value}
              aria-pressed={sort === value}
              onClick={() => {
                if (value !== sort && confirmDrafts()) setSort(value);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <Composer
        key={`composer:${sort}`}
        {...{ en, user, onLogin, inputRef }}
        onSave={(text) => create(text)}
      />
      {query.isLoading && <p role="status">{en ? 'Loading comments…' : '댓글을 불러오는 중…'}</p>}
      {query.error && (
        <div role="alert">
          <p>{en ? 'Could not load comments.' : '댓글을 불러오지 못했어요.'}</p>
          <button onClick={() => query.refetch()}>{en ? 'Retry' : '다시 시도'}</button>
        </div>
      )}
      {!query.isLoading && !query.error && !comments.length && (
        <p className="pp-review-comment-empty">
          {en ? 'Be the first to comment.' : '첫 댓글을 남겨보세요.'}
        </p>
      )}
      <div key={`comments:${sort}`}>
        {comments.map((c) => (
          <CommentRow
            key={c.id}
            comment={c}
            {...{ en, user, onLogin, onReport, run, refresh, updateCached }}
            onCreate={create}
            targetId={createdTarget ?? targetId}
          />
        ))}
      </div>
    </section>
  );
}
